/**
 * ListingCalendar Component
 * Integrates with provider availability API to show disabled dates
 */

import React, { useState, useEffect } from 'react';
import { phoenixTodayISO, getDisabledDates } from '../../util/availabilityUtils';

const ListingCalendar = ({ providerId, children, onDisabledDatesChange }) => {
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const enabled = process.env.REACT_APP_AVAILABILITY_CALENDAR_ENABLED === 'true';

  useEffect(() => {
    if (!enabled || !providerId) {
      setBookedDates([]);
      return;
    }

    const fetchAvailability = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`📅 Fetching availability for provider: ${providerId}`);
        
        const response = await fetch(`/api/availability/${providerId}`, {
          method: 'GET',
          credentials: 'include', // Include cookies for auth if needed
        });

        if (!response.ok) {
          if (response.status === 403) {
            console.log('📅 Availability calendar feature is disabled');
            setBookedDates([]);
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log(`📅 Received availability data:`, data);
        
        setBookedDates(data.bookedDates || []);
        
      } catch (err) {
        console.error('❌ Failed to fetch availability:', err);
        setError(err.message);
        setBookedDates([]); // Fail open - don't disable dates on error
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [enabled, providerId]);

  // Calculate disabled dates including past dates
  const disabledDates = getDisabledDates(bookedDates);
  const today = phoenixTodayISO();

  // Notify parent component when disabled dates change
  useEffect(() => {
    if (onDisabledDatesChange) {
      onDisabledDatesChange(disabledDates);
    }
  }, [disabledDates, onDisabledDatesChange]);

  // If we're using children as a render prop, pass the calendar data
  if (typeof children === 'function') {
    return children({
      disabledDates,
      bookedDates,
      minDate: today,
      loading,
      error,
      enabled
    });
  }

  // If children are React elements, clone them with calendar props
  if (React.Children.count(children) === 1) {
    const child = React.Children.only(children);
    return React.cloneElement(child, {
      disabledDates,
      minDate: today,
      // Preserve existing props
      ...child.props
    });
  }

  // Return children as-is if multiple or no calendar integration needed
  return children;
};

export default ListingCalendar;
